import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { useCallback, useEffect } from "react";
import { logger } from "../../log";
import { useCreateUpdateInfo } from "../hooks";
import { useCapgoStore, type Resource } from "./store";

const log = logger("capgo");

export function useRegisterCapGo() {
  useCapGoListeners();
  useNotifyAppReady();
}

export function useUpdateAvailableListener() {
  const createUpdateInfo = useCreateUpdateInfo();

  useEffect(() => {
    CapacitorUpdater.addListener("updateAvailable", (res) => {
      log.info("updateAvailable");
      createUpdateInfo(() => CapacitorUpdater.set(res.bundle));
    });
  }, [createUpdateInfo]);
}

function useNotifyAppReady() {
  useEffect(() => {
    log.info("notifyAppReady");
    CapacitorUpdater.notifyAppReady()
      .then((result) =>
        log.info("notifyAppReady success: " + JSON.stringify(result)),
      )
      .catch((error) => log.error("notifyAppReady error: " + error.message));
  }, []);
}

function useCapGoListeners() {
  useEffect(() => {
    const subscriptions = Promise.allSettled([
      CapacitorUpdater.addListener("download", (event) =>
        log.info("download: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("noNeedUpdate", (event) =>
        log.info("noNeedUpdate: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("updateAvailable", (event) =>
        log.info("updateAvailable: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("downloadComplete", (event) =>
        log.info("downloadComplete: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("breakingAvailable", (event) =>
        log.info("breakingAvailable: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("updateFailed", (event) =>
        log.info("updateFailed: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("downloadFailed", (event) =>
        log.info("downloadFailed: " + JSON.stringify(event)),
      ),
      CapacitorUpdater.addListener("appReloaded", () =>
        log.info("appReloaded"),
      ),
      CapacitorUpdater.addListener("appReady", (event) =>
        log.info("appReady: " + JSON.stringify(event)),
      ),
    ]);

    return () => {
      subscriptions.then((subs) =>
        Promise.allSettled(
          subs.map(async (s) => {
            if (s.status === "fulfilled") {
              await s.value.remove();
            }
          }),
        ),
      );
    };
  }, []);
}

export function useUpdateCapgo(): () => Promise<void> {
  const {
    setCurrent,
    setChannel,
    setDeviceId,
    setLatest,
    setBuiltinVersion,
    setPluginVersion,
  } = useCapgoStore();

  return useCallback(async () => {
    await Promise.all([
      updateResource(CapacitorUpdater.current, setCurrent),
      updateResource(CapacitorUpdater.getChannel, setChannel),
      updateResource(CapacitorUpdater.getDeviceId, setDeviceId),
      updateResource(CapacitorUpdater.getLatest, setLatest),
      updateResource(CapacitorUpdater.getBuiltinVersion, setBuiltinVersion),
      updateResource(CapacitorUpdater.getPluginVersion, setPluginVersion),
    ]);
  }, [
    setCurrent,
    setChannel,
    setDeviceId,
    setLatest,
    setBuiltinVersion,
    setPluginVersion,
  ]);
}

async function updateResource<T>(
  get: () => Promise<T>,
  set: (v: Resource<T>) => void,
): Promise<void> {
  set({ state: "loading" });

  try {
    const data = await get();
    set({ state: "ready", data });
  } catch (error) {
    log.warn("Error updating CapGo resource", error);
    set({ state: "error", error });
  }
}
